'use client';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { removeFromCart, updateQuantity, clearCart } from '@/store/slices/cartSlice';
import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import checked from '@/assets/checked.svg';
import cartBlack from '@/assets/cart-black.svg';
import cartWhite from '@/assets/cart-white.svg';
import starIcon from '@/assets/star2.svg';

export default function CartPage() {
  const dispatch = useAppDispatch();
  const { items, totalItems, totalPrice } = useAppSelector((state) => state.cart);
  const [isClearing, setIsClearing] = useState(false);

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    dispatch(updateQuantity({ itemId, quantity: newQuantity }));
  };

  const handleRemoveItem = (itemId: string) => {
    dispatch(removeFromCart(itemId));
  };

  const handleClearCart = () => {
    setIsClearing(true);
    dispatch(clearCart());
    setTimeout(() => setIsClearing(false), 1000);
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, i) => (
      <Image key={i} src={starIcon} alt="Star" width={14} height={14} />
    ));
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-[#F8FBF2] py-16">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Shopping Cart
              </h1>
              
              {/* Breadcrumb Navigation */}
              <nav className="flex justify-center items-center space-x-2 text-gray-700">
                <Link 
                  href="/" 
                  className="hover:text-green-600 transition-colors"
                >
                  Home
                </Link>
                <span className="mx-2 text-gray-400">›</span>
                <span className="text-gray-700">Cart</span>
              </nav>
            </div>
          </div>
        </section>

        {/* Empty Cart */}
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8 py-16">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-8 bg-gray-100 rounded-full flex items-center justify-center">
              <Image src={cartBlack} alt="Empty Cart" width={64} height={64} className="opacity-50" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Looks like you haven&apos;t added any products to your cart yet. Start shopping to find the perfect weight management solutions for you.
            </p>
            <Link
              href="/products"
              className="inline-flex items-center px-8 py-4 bg-[#7771B8] text-white font-semibold hover:bg-[#6B5B9A] transition-colors"
            >
              <Image src={cartWhite} alt="Cart" width={20} height={20} className="mr-2" />
              Start Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-[#F8FBF2] py-16">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Shopping Cart
            </h1>
            
            {/* Breadcrumb Navigation */}
            <nav className="flex justify-center items-center space-x-2 text-gray-700">
              <Link 
                href="/" 
                className="hover:text-green-600 transition-colors"
              >
                Home
              </Link>
              <span className="mx-2 text-gray-400">›</span>
              <span className="text-gray-700">Cart</span>
            </nav>
          </div>
        </div>
      </section>

      {/* Cart Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Cart Items ({totalItems})
              </h2>
              {items.length > 0 && (
                <button
                  onClick={handleClearCart}
                  disabled={isClearing}
                  className="text-red-600 hover:text-red-700 font-medium transition-colors disabled:opacity-50"
                >
                  {isClearing ? 'Clearing...' : 'Clear Cart'}
                </button>
              )}
            </div>

            <div className="space-y-6">
              {items.map((item) => (
                <div key={item.id} className="bg-white border border-gray-200 p-6 shadow-sm">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Product Image */}
                    <div className="w-full md:w-50 p-4 sm:h-50 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      {item.product.image ? (
                        <Image
                          src={item.product.image}
                          alt={item.product.name}
                          width={200}
                          height={300}
                          className="object-cover"
                        />
                      ) : (
                        <div className="bg-white border-2 border-gray-200 rounded-lg p-4 text-center">
                          <div className="bg-red-500 text-white text-xs font-bold py-1 px-2 rounded-t-lg -mt-4 mb-2">
                            UKWEIGHTLOSS
                          </div>
                          <div className="text-sm font-bold text-gray-800 mb-1">General Health</div>
                          <div className="text-xs font-semibold text-gray-600 mb-2">BLOOD TEST KIT</div>
                          <div className="text-xs text-gray-500">Get your results the next day</div>
                        </div>
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                          {item.product.name}
                        </h3>
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-red-600 hover:text-red-700 ml-4 flex-shrink-0"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      {/* Reviews */}
                      <div className="flex items-center space-x-1 mb-3">
                        {renderStars()}
                        <span className="text-sm text-gray-600 ml-1">200 Reviews</span>
                      </div>

                      {/* Price and Quantity */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="text-2xl font-bold text-[#7771B8]">
                          £{item.product.price.toFixed(0)}
                        </div>

                        {/* Quantity Selector */}
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-gray-700">Quantity:</span>
                          <div className="flex items-center border border-gray-300 rounded">
                            <button
                              onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                              className="px-3 py-2 text-lg text-black hover:bg-gray-100 bg-[#EBEBEB] rounded-l"
                            >
                              -
                            </button>
                            <span className="px-4 py-2 text-black border-x border-gray-300 min-w-[3rem] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                              className="px-3 py-2 text-black hover:bg-gray-100 bg-[#EBEBEB] rounded-r"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Subtotal */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Subtotal:</span>
                          <span className="text-lg font-semibold text-gray-900">
                            £{(item.product.price * item.quantity).toFixed(0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-6 sticky top-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal ({totalItems} items)</span>
                  <span className="font-medium">£{totalPrice.toFixed(0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium text-green-600">Free</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax</span>
                  <span className="font-medium">£0.00</span>
                </div>
                <div className="border-t border-gray-300 pt-4">
                  <div className="flex justify-between">
                    <span className="text-lg font-bold text-gray-900">Total</span>
                    <span className="text-lg font-bold text-[#7771B8]">£{totalPrice.toFixed(0)}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button className="w-full bg-[#7771B8] text-white py-4 px-6 font-semibold hover:bg-[#6B5B9A] transition-colors flex items-center justify-center space-x-2">
                  <Image src={checked} alt="Checkout" width={20} height={20} />
                  <span>Proceed to Checkout</span>
                </button>
                
                <Link
                  href="/products"
                  className="w-full bg-white text-gray-900 py-4 px-6 font-semibold border-2 border-gray-200 hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <Image src={cartBlack} alt="Continue Shopping" width={20} height={20} />
                  <span>Continue Shopping</span>
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t border-gray-300">
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Image src={checked} alt="Secure" width={16} height={16} />
                    <span className="text-sm text-gray-600">Secure Checkout</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Image src={checked} alt="Free Shipping" width={16} height={16} />
                    <span className="text-sm text-gray-600">Free Shipping</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <Image src={checked} alt="Money Back" width={16} height={16} />
                    <span className="text-sm text-gray-600">30-Day Money Back</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
